/**
 * Datos de Meta (formulario instantáneo e insights de anuncios) traducidos al
 * CRM. Funciones puras: ni red ni base de datos.
 *
 * El formulario «Bodas · fecha libre» pregunta, por este orden:
 *   fecha_boda (texto libre, vale «aún sin fecha») · lugar_boda · full_name · phone_number.
 * El mapeo es tolerante: si Juan renombra o añade preguntas, se reconocen por
 * su clave normalizada (sin tildes ni mayúsculas) y por palabras clave.
 */

/** Un campo de `field_data` tal y como lo devuelve la Graph API. */
export interface CampoMeta {
  name: string;
  values?: string[];
}

/** Un lead de `GET /{form_id}/leads`. */
export interface LeadMetaCrudo {
  id: string;
  created_time: string;
  field_data?: CampoMeta[];
  ad_id?: string;
  adset_id?: string;
  campaign_id?: string;
  form_id?: string;
}

/** El lead listo para `crm_importar_lead_meta`. */
export interface LeadMetaImportable {
  meta_lead_id: string;
  created_time: string;
  form_id: string;
  ad_id: string | null;
  adset_id: string | null;
  campaign_id: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  /** AAAA-MM-DD si la respuesta se entiende como una fecha concreta. */
  event_date: string | null;
  /** La respuesta tal cual («junio 2027», «aún sin fecha»). */
  event_date_text: string | null;
  location: string | null;
}

/** Qué campos del CRM se han podido rellenar (para el ensayo con leads reales). */
export type CampoCrm = "name" | "phone" | "email" | "event_date_text" | "location";

/** Una fila de `GET /act_{id}/insights` con `level=ad` y `time_increment=1`. */
export interface InsightCrudo {
  date_start: string;
  ad_id: string;
  ad_name?: string;
  adset_id?: string;
  adset_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  spend?: string;
  impressions?: string;
  clicks?: string;
  actions?: { action_type: string; value: string }[];
}

/** Una fila de `ad_metrics_daily`. */
export interface MetricaAnuncioDia {
  date: string;
  ad_id: string;
  ad_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  spend_cents: number;
  impressions: number;
  clicks: number;
  leads: number;
}

/* --- Formulario -------------------------------------------------------- */

/** "¿Cuándo es la boda?" → "cuando_es_la_boda". */
export function normalizarClave(clave: string): string {
  return clave
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

interface Regla {
  exactas: readonly string[];
  contiene: readonly string[];
}

const REGLAS: Record<CampoCrm | "first_name" | "last_name", Regla> = {
  name: { exactas: ["full_name", "nombre", "nombre_completo", "name"], contiene: ["nombre"] },
  first_name: { exactas: ["first_name"], contiene: [] },
  last_name: { exactas: ["last_name"], contiene: [] },
  phone: {
    exactas: ["phone_number", "phone", "telefono", "movil"],
    contiene: ["telefono", "phone", "movil", "whatsapp"],
  },
  email: { exactas: ["email", "correo", "correo_electronico"], contiene: ["email", "correo"] },
  event_date_text: {
    exactas: ["fecha_boda", "fecha", "fecha_de_la_boda", "wedding_date", "date"],
    contiene: ["fecha", "cuando", "date"],
  },
  location: {
    exactas: ["lugar_boda", "lugar", "ubicacion", "ciudad", "city", "location"],
    contiene: ["lugar", "donde", "finca", "ubicacion", "ciudad", "provincia"],
  },
};

function primerValor(campo: CampoMeta | undefined): string | null {
  const valor = campo?.values?.find((v) => v.trim() !== "");
  return valor ? valor.trim() : null;
}

/** Busca el campo por clave exacta y, si no, por palabra clave. Nunca usa un campo dos veces. */
function buscar(campos: Map<string, CampoMeta>, regla: Regla, usados: Set<string>): string | null {
  for (const clave of regla.exactas) {
    const campo = campos.get(clave);
    if (campo && !usados.has(clave) && primerValor(campo)) {
      usados.add(clave);
      return primerValor(campo);
    }
  }
  for (const [clave, campo] of campos) {
    if (usados.has(clave) || !regla.contiene.some((palabra) => clave.includes(palabra))) continue;
    const valor = primerValor(campo);
    if (valor) {
      usados.add(clave);
      return valor;
    }
  }
  return null;
}

/** Traduce un lead de Meta al formato del CRM. */
export function mapearLeadMeta(crudo: LeadMetaCrudo, formId: string): LeadMetaImportable {
  const campos = new Map((crudo.field_data ?? []).map((c) => [normalizarClave(c.name), c]));
  const usados = new Set<string>();
  const telefono = buscar(campos, REGLAS.phone, usados);
  const email = buscar(campos, REGLAS.email, usados);
  const fecha = buscar(campos, REGLAS.event_date_text, usados);
  const nombreCompleto = buscar(campos, REGLAS.name, usados);
  const nombre =
    nombreCompleto ??
    ([buscar(campos, REGLAS.first_name, usados), buscar(campos, REGLAS.last_name, usados)]
      .filter(Boolean)
      .join(" ") ||
      null);
  const lugar = buscar(campos, REGLAS.location, usados);
  return {
    meta_lead_id: crudo.id,
    created_time: crudo.created_time,
    form_id: crudo.form_id ?? formId,
    ad_id: crudo.ad_id ?? null,
    adset_id: crudo.adset_id ?? null,
    campaign_id: crudo.campaign_id ?? null,
    name: nombre,
    phone: telefono,
    email: email?.toLowerCase() ?? null,
    event_date: fecha ? parsearFechaBoda(fecha) : null,
    event_date_text: fecha,
    location: lugar,
  };
}

/** Campos del CRM que han quedado rellenos. Sirve para revisar el mapeo sin ver los datos. */
export function camposRellenos(lead: LeadMetaImportable): CampoCrm[] {
  const campos: CampoCrm[] = ["name", "phone", "email", "event_date_text", "location"];
  return campos.filter((campo) => Boolean(lead[campo]));
}

const MESES: Record<string, number> = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  setiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

function fechaValida(anio: number, mes: number, dia: number): string | null {
  if (anio < 100) anio += 2000;
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  if (
    fecha.getUTCFullYear() !== anio ||
    fecha.getUTCMonth() !== mes - 1 ||
    fecha.getUTCDate() !== dia
  )
    return null;
  return fecha.toISOString().slice(0, 10);
}

/**
 * Respuesta libre → AAAA-MM-DD, solo si nombra un día concreto:
 * "12/06/2027", "12-6-27", "2027-06-12", "12 de junio de 2027", "sábado 12 junio 2027".
 * "Junio 2027" o "aún sin fecha" devuelven null (el texto se guarda aparte).
 */
export function parsearFechaBoda(texto: string): string | null {
  const limpio = normalizarClave(texto).replace(/_/g, " ");
  const iso = limpio.match(/\b(\d{4}) (\d{1,2}) (\d{1,2})\b/);
  if (iso) return fechaValida(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const numerica = limpio.match(/\b(\d{1,2}) (\d{1,2}) (\d{4}|\d{2})\b/);
  if (numerica) return fechaValida(Number(numerica[3]), Number(numerica[2]), Number(numerica[1]));
  const conMes = limpio.match(/\b(\d{1,2}) (?:de )?([a-z]+) (?:de |del )?(\d{4})\b/);
  if (conMes && MESES[conMes[2]])
    return fechaValida(Number(conMes[3]), MESES[conMes[2]], Number(conMes[1]));
  return null;
}

/* --- Insights ---------------------------------------------------------- */

/** `lead` es el total de Meta; los otros son el detalle por si no viene el total. */
const ACCIONES_LEAD = ["lead", "onsite_conversion.lead_grouped", "leadgen_grouped"] as const;

function entero(valor: string | undefined): number {
  const numero = Number(valor ?? 0);
  return Number.isFinite(numero) ? Math.max(Math.round(numero), 0) : 0;
}

export function mapearInsight(crudo: InsightCrudo): MetricaAnuncioDia {
  const acciones = crudo.actions ?? [];
  const accionLead = ACCIONES_LEAD.map((tipo) => acciones.find((a) => a.action_type === tipo)).find(
    Boolean,
  );
  return {
    date: crudo.date_start,
    ad_id: crudo.ad_id,
    ad_name: crudo.ad_name ?? null,
    adset_id: crudo.adset_id ?? null,
    adset_name: crudo.adset_name ?? null,
    campaign_id: crudo.campaign_id ?? null,
    campaign_name: crudo.campaign_name ?? null,
    spend_cents: Math.max(Math.round(Number(crudo.spend ?? 0) * 100), 0) || 0,
    impressions: entero(crudo.impressions),
    clicks: entero(crudo.clicks),
    leads: entero(accionLead?.value),
  };
}

/* --- Aviso ------------------------------------------------------------- */

const formatoFecha = new Intl.DateTimeFormat("es-ES", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** "Nuevo lead de Meta: Marta · boda 12 jun 2027 · Ronda · llámale ya". */
export function asuntoAvisoLead(lead: LeadMetaImportable): string {
  const fecha = lead.event_date
    ? formatoFecha.format(new Date(`${lead.event_date}T00:00:00Z`))
    : (lead.event_date_text ?? "sin fecha");
  return [
    `Nuevo lead de Meta: ${lead.name ?? "sin nombre"}`,
    `boda ${fecha}`,
    lead.location ?? "lugar sin indicar",
    "llámale ya",
  ].join(" · ");
}

/* --- Fechas de Madrid -------------------------------------------------- */

const formatoDiaMadrid = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Madrid",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** El día de calendario en Madrid, AAAA-MM-DD. */
export function diaMadrid(momento: Date): string {
  return formatoDiaMadrid.format(momento);
}

/** Suma (o resta) días a un AAAA-MM-DD. */
export function sumarDias(dia: string, dias: number): string {
  const fecha = new Date(`${dia}T00:00:00Z`);
  fecha.setUTCDate(fecha.getUTCDate() + dias);
  return fecha.toISOString().slice(0, 10);
}
